import React, { useRef, useEffect, useState } from "react";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import "./DMapStyle.css";
import { bbox } from "@turf/turf";
import { getProjectCoordinates } from '../../../../utils/mapDataMapping';
import { Tipligheri, Maipith, Dwariknagar, SHIVGURI_CR_Demarcation2025, MER_CR_Demarcation2025, PS0000149 } from "../../../../mock/dummyGeoJson";
import { FeatureCollection } from "geojson";
import LayerControl from "./LayerControl";
import { getMapStyleUrl, getMapAttribution, getMapStyleOptions } from '../../../../utils/mapStyles';
import { getDeviceLayersForProject } from '../../../../utils/mapLayerUtils';

const escapeHtml = (value: any) =>
  String(value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] || char));

const getFeatureInfoHtml = (properties: any) =>
  Object.entries(properties || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => `<div><strong>${escapeHtml(key)}:</strong> ${escapeHtml(value)}</div>`)
    .join("");

const DMapEnvAnalysis: React.FC = () => {
  const [siteDropDownDetails, setSiteDropDownDetails] = useState<any>(
    JSON.parse(localStorage.getItem("SelectedDropDownEnvSite") || "{}")
  );

  const siteName = siteDropDownDetails?.site_name || "Dwariknagar";
  const DEVICE_LAYERS = getDeviceLayersForProject(siteName);

  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [boundaryData, setBoundaryData] = useState<FeatureCollection | null>(null);
  const [mapStyle, setMapStyle] = useState<string>('satellite');
  const [activeLayers, setActiveLayers] = useState<string[]>([]);
  const [layers, setLayers] = useState<{ id: string; name: string; opacity: number }[]>([]);
  const mapInitialized = useRef<boolean>(false);
  const tileLayerRef = useRef<L.TileLayer | null>(null);

  const updateBoundaryFromLocalStorage = () => {
    let newBoundary = null;
    const currentSiteName = siteDropDownDetails?.site_name;

    if (currentSiteName === "Dwariknagar" || currentSiteName === "Drawiknagar") newBoundary = Dwariknagar;
    else if (currentSiteName === "Tipligheri") newBoundary = Tipligheri;
    else if (currentSiteName === "Maipith") newBoundary = Maipith;
    else if (currentSiteName === "SHIVGURI_CR_Demarcation2025" || currentSiteName === "Shivguri") newBoundary = SHIVGURI_CR_Demarcation2025;
    else if (currentSiteName === "Mer") newBoundary = MER_CR_Demarcation2025;
    else if (currentSiteName === "PS0000149") newBoundary = PS0000149;
    
    console.log(`[DMapEnvAnalysis] Site Name: ${siteName}, Found Boundary: ${!!newBoundary}`);

    setBoundaryData(newBoundary as unknown as FeatureCollection);
  };

  const initializeMap = (data: FeatureCollection) => {
    if (!mapContainer.current) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }

    const projectName = siteDropDownDetails.site_name || "Dwariknagar";
    const center = getProjectCoordinates(projectName);
    const centerLatLng: [number, number] = Array.isArray(center) && center.length >= 2 ? [Number(center[1]), Number(center[0])] : [0, 0];

    mapRef.current = L.map(mapContainer.current as HTMLDivElement, { center: centerLatLng, zoom: 13, zoomControl: true });
    tileLayerRef.current = L.tileLayer(getMapStyleUrl(mapStyle), getMapStyleOptions(mapStyle)).addTo(mapRef.current);

    const boundaryLayer = L.geoJSON(data as any, {
      style: () => ({ fillColor: '#90EE90', fillOpacity: 0.3, color: '#006400', weight: 2 }),
      onEachFeature: (feature: any, layer: any) => { if (feature.properties) layer.bindPopup(Object.entries(feature.properties).map(([k, v]) => `<strong>${k}:</strong> ${v}`).join('<br/>')); }
    }).addTo(mapRef.current);

    const deviceLayerGroups: Record<string, L.LayerGroup> = {};
    DEVICE_LAYERS.forEach(({ type }) => { deviceLayerGroups[`device-${type.replace(/\s+/g, '-').toLowerCase()}`] = L.layerGroup(); });

    try {
      const features = (data as any).features || [];
      features.forEach((f: any) => {
        if (!f || !f.geometry) return;
        if (f.geometry.type === 'Point') {
          const coords = f.geometry.coordinates;
          const latlng: [number, number] = [coords[1], coords[0]];
          const type = f.properties?.Type || f.properties?.type || '';
          const layerKey = `device-${String(type).replace(/\s+/g, '-').toLowerCase()}`;
          const color = DEVICE_LAYERS.find(dl => dl.type === type)?.color || '#ff5722';
          const marker = L.circleMarker(latlng, { radius: 10, fillColor: color, color: '#fff', weight: 2, fillOpacity: 1 });
          if (f.properties) marker.bindPopup(Object.entries(f.properties).map(([k, v]) => `<strong>${k}:</strong> ${v}`).join('<br/>'));
          deviceLayerGroups[layerKey]?.addLayer(marker);
        }
      });
    } catch (e) { console.warn('Failed to populate device layers', e); }

    (mapRef.current as any)._deviceLayerGroups = deviceLayerGroups;
    (mapRef.current as any)._boundaryLayer = boundaryLayer;

    Object.values(deviceLayerGroups).forEach(g => g.addTo(mapRef.current as L.Map));

    setLayers([
      { id: 'boundary-fill', name: 'Site Boundary', opacity: 0.3 },
      ...DEVICE_LAYERS.map(d => ({ id: `device-${d.type.replace(/\s+/g, '-').toLowerCase()}`, name: d.label, opacity: 1 })),
    ]);
    setActiveLayers(['boundary-fill', ...DEVICE_LAYERS.map(d => `device-${d.type.replace(/\s+/g, '-').toLowerCase()}`)]);

    try {
      const siteBbox = bbox(data as unknown as FeatureCollection);
      const bounds = L.latLngBounds([[siteBbox[1], siteBbox[0]], [siteBbox[3], siteBbox[2]]]);
      mapRef.current?.fitBounds(bounds, { padding: [40, 40] });
    } catch (err) { console.error("[DMapEnvAnalysis] fitBounds error:", err); }
  };

  // ✅ Single unified effect — no more race condition, no polling
  useEffect(() => {
    const intervalId = setInterval(() => {
      const updated = JSON.parse(localStorage.getItem("SelectedDropDownCarbonSite") || "{}");
      if (JSON.stringify(updated) !== JSON.stringify(siteDropDownDetails)) setSiteDropDownDetails(updated);
    }, 500);
    return () => clearInterval(intervalId);
  }, [siteDropDownDetails]);

  useEffect(() => { updateBoundaryFromLocalStorage(); }, [siteDropDownDetails]);

  useEffect(() => { 
    if (boundaryData && !mapInitialized.current) {
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
        }
        initializeMap(boundaryData);
        mapInitialized.current = true;
    }

    return () => {
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
            mapInitialized.current = false;
        }
    };
  }, [boundaryData]);

  // Handle map style changes without recreating the map
  useEffect(() => {
    if (!mapRef.current || !mapInitialized.current || !tileLayerRef.current) return;

    // Remove old tile layer
    mapRef.current.removeLayer(tileLayerRef.current);

    // Add new tile layer
    tileLayerRef.current = L.tileLayer(getMapStyleUrl(mapStyle), getMapStyleOptions(mapStyle)).addTo(mapRef.current);
  }, [mapStyle]);

  const toggleLayerVisibility = (layer: string) => {
    const isActive = activeLayers.includes(layer);
    setActiveLayers(isActive ? activeLayers.filter(id => id !== layer) : [...activeLayers, layer]);
    const map = mapRef.current as any;
    if (!map) return;
    const deviceGroups: Record<string, L.LayerGroup> = map._deviceLayerGroups || {};
    if (deviceGroups[layer]) {
      if (isActive) map.removeLayer(deviceGroups[layer]); else deviceGroups[layer].addTo(map);
      return;
    }
    const boundaryLayer: L.Layer = map._boundaryLayer;
    if (layer === 'boundary-fill' && boundaryLayer) {
      if (isActive) map.removeLayer(boundaryLayer); else (boundaryLayer as any).addTo(map);
    }
  };

  const handleOpacityChange = (layerId: string, newOpacity: number) => {
    setLayers(prev => prev.map(l => l.id === layerId ? { ...l, opacity: newOpacity } : l));
    const map = mapRef.current as any;
    if (!map) return;
    if (layerId === 'boundary-fill') {
      const b = map._boundaryLayer as L.GeoJSON | undefined;
      if (b) b.setStyle({ fillOpacity: newOpacity });
      return;
    }
    const deviceGroups: Record<string, L.LayerGroup> = map._deviceLayerGroups || {};
    const group = deviceGroups[layerId];
    if (group) group.eachLayer((lyr: any) => { if (lyr && typeof lyr.setStyle === 'function') try { lyr.setStyle({ fillOpacity: newOpacity, opacity: newOpacity }); } catch(e){} });
  };

  return (
    <div style={{ position: "relative", height: "65vh", width: "99%" }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '8px 15px', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: '13px' }}>Devices:</span>
        {DEVICE_LAYERS.map(({ type, color, label }) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
            <div style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: color, border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.35)', flexShrink: 0 }} />
            <span>{label}</span>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
          <div style={{ width: '14px', height: '14px', backgroundColor: 'rgba(144,238,144,0.6)', border: '1.5px solid #006400', borderRadius: '2px' }} />
          <span>Site Boundary</span>
        </div>
      </div>
      <div ref={mapContainer} style={{ position: 'absolute', top: 40, bottom: 0, width: '100%' }} />
      <LayerControl layers={layers} activeLayers={activeLayers} toggleLayerVisibility={toggleLayerVisibility} handleOpacityChange={handleOpacityChange} mapStyle={mapStyle} setMapStyle={setMapStyle} />
    </div>
  );
};

export default DMapEnvAnalysis;
